import { useState } from "react";
import type { ActorDTO, RunDTO } from "@roomate/core";
import { useActiveAgentRuns, useSandboxAction, useSandboxStatus, useStopRun } from "../api.ts";
import { Pill } from "../calm.tsx";
import { Button, ErrorNote, useNow } from "../ui.tsx";
import {
  canCheckLogin,
  canResume,
  connectCommand,
  loginLabel,
  needsReconnect,
  panelRun,
  RECONNECT_TITLE,
  sandboxStateLabel,
  snapshotAge,
  stopPhase,
  type SandboxState,
  type SandboxStatusDTO,
} from "../lib/sandbox.ts";

const STATE_TONE: Record<SandboxState, "live" | "neutral" | "signal" | "danger"> = { pending: "signal", running: "live", stopping: "signal", stopped: "neutral", failed: "danger" };

const PHASE_LABEL = { live: "Run is live", stopping: "Stopping…", stopped: "Run stopped", ended: "Run ended before the stop took effect" } as const;

/** Writes the exact connect command to the clipboard; resolves to whether the browser allowed it. */
export async function copyConnectCommand(sandboxName: string | null): Promise<boolean> {
  const command = connectCommand(sandboxName);
  if (!command) return false;
  try {
    await navigator.clipboard.writeText(command);
    return true;
  } catch {
    return false;
  }
}

/** The sandbox part of a remote agent's card. Renders nothing for a local agent or a hub without the sandbox route. */
export function SandboxPanel({ actor, admin }: { actor: ActorDTO; admin: boolean }) {
  const remote = actor.kind === "agent" && actor.host === "vercel-sandbox";
  const status = useSandboxStatus(actor.id, remote);
  const runs = useActiveAgentRuns(actor.id, remote);
  // Ticks so the snapshot age and the stopped-run notice keep moving between polls (no re-render when a poll is unchanged).
  const now = useNow(30_000);
  if (!remote || !status.data) return null;
  return <SandboxPanelBody actor={actor} admin={admin} status={status.data} runs={runs.data ?? []} now={now} />;
}

export function SandboxPanelBody({ actor, admin, status, runs, now = Date.now() }: { actor: ActorDTO; admin: boolean; status: SandboxStatusDTO; runs: RunDTO[]; now?: number }) {
  const [copied, setCopied] = useState<"yes" | "no" | null>(null);
  const resume = useSandboxAction(actor.id, "resume");
  const check = useSandboxAction(actor.id, "check-login");
  const stop = useStopRun();
  const run = panelRun(runs, now);
  const phase = run ? stopPhase(run) : null;
  const name = status.sandboxName ?? actor.hostSandbox;
  return (
    <div className="mt-3.5 space-y-2 rounded-xl border border-rule p-3 text-[12.5px]" data-testid="sandbox-panel" data-state={status.state ?? "none"}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Pill tone={status.state ? STATE_TONE[status.state] : "neutral"} testId="sandbox-state">
          {sandboxStateLabel(status.state)}
        </Pill>
        <Pill tone={status.loginState === "ok" ? "live" : status.loginState ? "danger" : "neutral"} testId="sandbox-login">
          {loginLabel(status.loginState)}
        </Pill>
        <span className="text-ink-3" data-testid="sandbox-snapshot">
          snapshot: {snapshotAge(status.lastSnapshotAt, now)}
        </span>
      </div>
      {phase && (
        <p data-testid="sandbox-run-phase" data-phase={phase} className="text-ink-2">
          {PHASE_LABEL[phase]}
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        <Button
          variant="ghost"
          className="px-2 text-[12px]"
          data-testid="sandbox-connect"
          disabled={!name}
          title={name ? connectCommand(name)! : "no sandbox yet"}
          onClick={async () => setCopied((await copyConnectCommand(name)) ? "yes" : "no")}
        >
          {copied === "yes" ? "Copied" : "Copy connect command"}
        </Button>
        {canCheckLogin(status) && (
          <Button variant="ghost" className="px-2 text-[12px]" data-testid="sandbox-check-login" disabled={check.isPending} onClick={() => check.mutate()}>
            {check.isPending ? "Checking…" : "Check login"}
          </Button>
        )}
        {admin && run && phase === "live" && (
          <Button variant="danger" className="px-2 text-[12px]" data-testid="sandbox-stop-now" disabled={stop.isPending} onClick={() => stop.mutate(run.id)}>
            Stop now
          </Button>
        )}
        {admin && actor.active && canResume(status.state) && (
          <Button variant="ghost" className="px-2 text-[12px]" data-testid="sandbox-resume" disabled={resume.isPending} onClick={() => resume.mutate()}>
            Resume
          </Button>
        )}
      </div>
      {copied === "no" && <p className="text-ink-3">Couldn't reach the clipboard. Run: <code className="font-mono">{connectCommand(name)}</code></p>}
      <ErrorNote error={stop.error ?? resume.error ?? check.error} />
    </div>
  );
}

/** Bridge item shown while a remote agent's sandbox has no usable login. It clears once a login check succeeds. */
export function ReconnectItem({ actor, sandboxName }: { actor: ActorDTO; sandboxName: string | null }) {
  const check = useSandboxAction(actor.id, "check-login");
  const name = sandboxName ?? actor.hostSandbox;
  const [copied, setCopied] = useState<"yes" | "no" | null>(null);
  return (
    <div data-testid="reconnect-item" className="glass rounded-[22px] p-6">
      <p className="text-[13px] text-ink-2">
        <span className="rounded-full bg-paper-2 px-2.5 py-0.5 text-[11.5px] font-medium">Login</span> {actor.name}
      </p>
      <h2 className="mt-3 text-[20px] font-medium leading-snug">{RECONNECT_TITLE}</h2>
      <p className="mt-2 text-[14px] text-ink-2">
        Run <code className="font-mono">{connectCommand(name) ?? "sandbox connect <name>"}</code> in a terminal, then <code className="font-mono">claude</code> and <code className="font-mono">/login</code> inside it.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="reconnect-copy" disabled={!name} onClick={async () => setCopied((await copyConnectCommand(name)) ? "yes" : "no")}>
          {copied === "yes" ? "Copied" : "Copy connect command"}
        </Button>
        <Button variant="primary" data-testid="reconnect-check" disabled={check.isPending} onClick={() => check.mutate()}>
          {check.isPending ? "Checking…" : "Check login"}
        </Button>
      </div>
      {check.data && needsReconnect(check.data) && <p className="mt-2 text-[13px] text-ink-3">Still {loginLabel(check.data.loginState).toLowerCase()}.</p>}
      <ErrorNote error={check.error} />
    </div>
  );
}
